/**
 * Nouvelle proforma — moteur de devis SaaS (lignes, totaux, conditions, PDF serveur).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  FileText,
  Loader2,
  Paperclip,
  Plus,
  Printer,
  Save,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { api } from "../../lib/axios";
import SelectClient from "../../components/SelectClient";
import {
  calculateLineTotal,
  calculateProformaTotals,
  type ProformaLineInput,
} from "../../utils/proformaCalculations";
import { validateProformaDraft } from "../../utils/proformaValidation";
import {
  CommercialProformaPreview,
  type CommercialProformaPreviewDto,
} from "./CommercialProformaPreview";
import { printElementHtml } from "../../utils/printDocument";

export const TYPE_METIER_OPTIONS = [
  { value: "DEVIS_CLIENT", label: "Devis client" },
  { value: "GROS_TRAVAUX", label: "Gros travaux" },
  { value: "CHANTIER", label: "Chantier" },
  { value: "URGENCE_PAYANTE", label: "Urgence payante" },
  { value: "NETTOYAGE_EXCEPTIONNEL", label: "Nettoyage exceptionnel" },
  { value: "COLLECTE_SPECIALE", label: "Collecte spéciale" },
  { value: "HORS_ABONNEMENT", label: "Hors abonnement" },
] as const;

type Devise = "USD" | "CDF";

type LineRow = {
  key: string;
  description: string;
  quantite: number;
  prixUnitaire: number;
  devise: Devise;
  remise: number;
};

function newLine(devise: Devise): LineRow {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    description: "",
    quantite: 1,
    prixUnitaire: 0,
    devise,
    remise: 0,
  };
}

function parseConfigTva(cfg: Record<string, unknown> | null): number {
  if (!cfg) return 16;
  const raw = cfg.tauxTva ?? cfg.tauxTVA;
  const n = typeof raw === "number" ? raw : Number(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : 16;
}

export default function NewProforma(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configTva, setConfigTva] = useState(16);

  const [clientId, setClientId] = useState("");
  const [typeMetier, setTypeMetier] = useState<string>("DEVIS_CLIENT");
  const [objet, setObjet] = useState("");
  const [commentaireMetier, setCommentaireMetier] = useState("");
  const [missionId, setMissionId] = useState<number | "">("");
  const [modeFinancement, setModeFinancement] = useState<
    "CLIENT_DIRECT" | "BUDGET_GLOBAL" | "SUBVENTION"
  >("CLIENT_DIRECT");
  const [deviseDefaut, setDeviseDefaut] = useState<Devise>("USD");
  const [dateEmission, setDateEmission] = useState(() => new Date().toISOString().slice(0, 10));

  const [lines, setLines] = useState<LineRow[]>(() => [newLine("USD")]);

  const [validiteProformaJours, setValiditeProformaJours] = useState<number>(30);
  const [delaiPaiementJours, setDelaiPaiementJours] = useState<number>(30);
  const [conditionsPaiement, setConditionsPaiement] = useState("");
  const [notesClient, setNotesClient] = useState("");
  const [mentionSpecifique, setMentionSpecifique] = useState("");

  const [exonereTva, setExonereTva] = useState(false);
  const [motifExoneration, setMotifExoneration] = useState("");

  const [attachment, setAttachment] = useState<File | null>(null);
  const [mentionEntreprise, setMentionEntreprise] = useState("");

  const [missions, setMissions] = useState<
    { id: number; numero: string; titre: string; date: string; zoneDepart: string }[]
  >([]);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDto, setPreviewDto] = useState<CommercialProformaPreviewDto | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get<Record<string, unknown>>("/configuration");
        if (!alive) return;
        setConfigTva(parseConfigTva(data));
        const d = data.deviseDefaut ?? data.devise;
        if (d === "CDF" || d === "USD") setDeviseDefaut(d);
        const cond =
          typeof data.conditionsPaiement === "string" ? data.conditionsPaiement : "";
        const del =
          typeof data.delaiPaiementJours === "number"
            ? data.delaiPaiementJours
            : Number(data.delaiPaiementJours) || 30;
        setConditionsPaiement(cond);
        setDelaiPaiementJours(Number.isFinite(del) ? del : 30);
        setMentionEntreprise(
          typeof data.mentionProforma === "string"
            ? data.mentionProforma
            : typeof data.mentionsLegales === "string"
              ? data.mentionsLegales
              : ""
        );
      } catch {
        if (alive) setConfigTva(16);
      } finally {
        if (alive) setLoadingConfig(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const qClient = searchParams.get("clientId");
    const qMission = searchParams.get("missionId");
    if (qClient && !clientId) setClientId(qClient);
    if (qMission && missionId === "") {
      const m = Number(qMission);
      if (Number.isFinite(m)) setMissionId(m);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL une fois
  }, [searchParams]);

  useEffect(() => {
    if (!clientId) {
      setMissions([]);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(
          `/missions?clientId=${clientId}&sansProforma=true`
        );
        if (alive) setMissions(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setMissions([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [clientId]);

  const linesForCalc: ProformaLineInput[] = useMemo(
    () =>
      lines.map((l) => ({
        quantite: l.quantite,
        prixUnitaire: l.prixUnitaire,
        remise: l.remise,
      })),
    [lines]
  );

  const effectiveTvaRate = exonereTva ? 0 : configTva;

  const totals = useMemo(
    () =>
      calculateProformaTotals(linesForCalc, effectiveTvaRate, {
        exonereTva,
      }),
    [linesForCalc, effectiveTvaRate, exonereTva]
  );

  const validationState = useMemo(
    () =>
      validateProformaDraft({
        clientId,
        typeMetier,
        objet,
        missionId,
        commentaireMetier,
        validiteProformaJours,
        conditionsPaiement,
        exonereTva,
        motifExoneration,
        totalTTC: totals.totalTTC,
        lignes: lines.map((l) => ({
          description: l.description,
          quantite: l.quantite,
          prixUnitaire: l.prixUnitaire,
          remise: l.remise,
        })),
      }),
    [
      clientId,
      typeMetier,
      objet,
      missionId,
      commentaireMetier,
      validiteProformaJours,
      conditionsPaiement,
      exonereTva,
      motifExoneration,
      totals.totalTTC,
      lines,
    ]
  );

  const terrainRequis = typeMetier !== "" && typeMetier !== "DEVIS_CLIENT";

  const clearFieldError = (k: string) => {
    setFieldErrors((prev) => {
      const n = { ...prev };
      delete n[k];
      return n;
    });
  };

  const validateForm = useCallback((): boolean => {
    const err: Record<string, string> = {};
    if (!clientId.trim()) err.clientId = "Client obligatoire.";
    if (!typeMetier.trim()) err.typeMetier = "Type métier obligatoire.";
    if (!objet.trim()) err.objet = "Objet obligatoire.";
    if (lines.length === 0) err.lignes = "Ajoutez au moins une ligne de prestation.";
    for (let i = 0; i < lines.length; i++) {
      const L = lines[i];
      if (!L.description.trim()) err[`ligne_${i}_desc`] = "Description obligatoire.";
      if (!(L.quantite > 0)) err[`ligne_${i}_qte`] = "Quantité > 0.";
      if (L.prixUnitaire <= 0) err[`ligne_${i}_pu`] = "Prix unitaire > 0.";
      if (L.remise < 0) err[`ligne_${i}_rem`] = "Remise invalide.";
    }
    if (exonereTva && !motifExoneration.trim()) {
      err.motifExoneration = "Motif obligatoire en cas d’exonération TVA.";
    }
    if (totals.totalTTC <= 0) err.totaux = "Le total TTC doit être strictement positif.";
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  }, [clientId, typeMetier, objet, lines, exonereTva, motifExoneration, totals.totalTTC]);

  const buildPayload = (intent: "brouillon" | "finaliser" | "envoyer") => ({
    clientId,
    typeMetier,
    objet: objet.trim(),
    modeFinancement,
    devise: deviseDefaut,
    dateEmission,
    intent,
    exonereTva,
    missionId: missionId === "" ? undefined : missionId,
    validiteProformaJours,
    delaiPaiementJours,
    conditionsPaiement: conditionsPaiement.trim() || undefined,
    notesClient: notesClient.trim() || undefined,
    mentionSpecifique: mentionSpecifique.trim() || undefined,
    mentionEntreprise: mentionEntreprise.trim() || undefined,
    motifExoneration: exonereTva ? motifExoneration.trim() || undefined : undefined,
    lignes: lines.map((l) => ({
      description: l.description.trim(),
      quantite: l.quantite,
      prixUnitaire: l.prixUnitaire,
      remise: l.remise,
      devise: l.devise,
    })),
  });

  const submit = async (intent: "brouillon" | "finaliser" | "envoyer") => {
    if (!validateForm()) return;
    setSubmitting(true);
    setFieldErrors({});
    try {
      const { data } = await api.post<{ success?: boolean; proforma?: { id: number; numero: string } }>(
        "/proformas",
        buildPayload(intent)
      );
      const proforma = data.proforma;
      if (!proforma?.id) throw new Error("CREATE_FAILED");
      if (intent !== "brouillon") {
        const pdf = await api.post<{ pdfUrl?: string; status?: string }>(`/proformas/${proforma.id}/generate-pdf`);
        if (pdf.data.pdfUrl) window.open(pdf.data.pdfUrl, "_blank", "noopener,noreferrer");
      }
      navigate(`/admin/finance/proformas`, {
        state: { createdId: proforma.id, numero: proforma.numero },
      });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string; details?: unknown } } };
      const msg = ax.response?.data?.error ?? "Création impossible.";
      setFieldErrors({ _submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post<{ success?: boolean; data?: CommercialProformaPreviewDto }>(
        "/proformas/preview",
        buildPayload("brouillon")
      );
      if (!data.data) throw new Error("NO_PREVIEW");
      setPreviewDto(data.data);
    } catch {
      setFieldErrors((p) => ({
        ...p,
        _preview: "Impossible de générer l’aperçu.",
      }));
    } finally {
      setPreviewLoading(false);
    }
  };

  const printPreviewDocument = () => {
    const el = document.getElementById("proforma-preview-print-root");
    if (!el) return;
    printElementHtml(el, "Aperçu proforma");
  };

  const closePreview = () => {
    setPreviewDto(null);
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 text-slate-100 md:px-8">
      <div className="w-full">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-emerald-400" />
            <div>
              <h1 className="text-2xl font-bold text-white md:text-3xl">Nouvelle proforma</h1>
              <p className="text-sm text-slate-500">Devis commercial structuré — totaux recalculés côté serveur.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/finance/proformas")}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4" /> Liste
          </button>
        </div>

        {loadingConfig ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Chargement configuration…
          </div>
        ) : (
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start lg:gap-8">
            <div className="space-y-8">
              {/* A — Client */}
              <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl ring-1 ring-white/5">
                <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-400/90">
                  A. Informations client
                </h2>
                <div className="mt-4 space-y-4">
                  <div>
                    <SelectClient value={clientId} onChange={(id) => { setClientId(id); clearFieldError("clientId"); }} />
                    {fieldErrors.clientId ? (
                      <p className="mt-1 text-xs text-rose-400">{fieldErrors.clientId}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">Type métier *</label>
                    <select
                      value={typeMetier}
                      onChange={(e) => {
                        setTypeMetier(e.target.value);
                        clearFieldError("typeMetier");
                      }}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm outline-none ring-emerald-500/0 focus:ring-2"
                    >
                      {TYPE_METIER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {terrainRequis ? (
                      <div className="mt-2 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span>
                          Cette proforma nécessitera un passage exceptionnel avant facturation si prestation
                          terrain (après validation client).
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">Objet *</label>
                    <input
                      value={objet}
                      onChange={(e) => {
                        setObjet(e.target.value);
                        clearFieldError("objet");
                      }}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                      placeholder="Objet du devis"
                    />
                    {fieldErrors.objet ? (
                      <p className="mt-1 text-xs text-rose-400">{fieldErrors.objet}</p>
                    ) : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">Commentaire métier</label>
                    <textarea
                      value={commentaireMetier}
                      onChange={(e) => setCommentaireMetier(e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                      placeholder="Contexte, contraintes…"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">Mission liée</label>
                    <select
                      value={missionId === "" ? "" : String(missionId)}
                      onChange={(e) =>
                        setMissionId(e.target.value ? Number(e.target.value) : "")
                      }
                      disabled={!clientId}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm disabled:opacity-50"
                    >
                      <option value="">
                        {clientId ? "— Aucune mission —" : "Choisissez un client d’abord"}
                      </option>
                      {missions.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.numero} — {m.titre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-400">Mode financement</label>
                      <select
                        value={modeFinancement}
                        onChange={(e) =>
                          setModeFinancement(
                            e.target.value as "CLIENT_DIRECT" | "BUDGET_GLOBAL" | "SUBVENTION"
                          )
                        }
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                      >
                        <option value="CLIENT_DIRECT">Client direct</option>
                        <option value="BUDGET_GLOBAL">Budget global</option>
                        <option value="SUBVENTION">Subvention</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-400">Date d’émission</label>
                      <input
                        type="date"
                        value={dateEmission}
                        onChange={(e) => setDateEmission(e.target.value)}
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* B — Lignes */}
              <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl ring-1 ring-white/5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-400/90">
                    B. Lignes de prestation
                  </h2>
                  <button
                    type="button"
                    onClick={() =>
                      setLines((prev) => [...prev, newLine(deviseDefaut)])
                    }
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
                  >
                    <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
                  </button>
                </div>
                {fieldErrors.lignes ? (
                  <p className="mt-2 text-xs text-rose-400">{fieldErrors.lignes}</p>
                ) : null}

                <div className="mt-4 overflow-x-auto">
                  <table className="w-full min-w-[720px] border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-slate-800 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        <th className="pb-2 pr-2">Description *</th>
                        <th className="pb-2 pr-2 w-24">Qté *</th>
                        <th className="pb-2 pr-2 w-28">P.U. *</th>
                        <th className="pb-2 pr-2 w-24">Devise</th>
                        <th className="pb-2 pr-2 w-24">Remise</th>
                        <th className="pb-2 pr-2 w-28 text-right">Total ligne</th>
                        <th className="pb-2 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((row, i) => (
                        <tr key={row.key} className="border-b border-slate-800/80">
                          <td className="py-2 pr-2 align-top">
                            <input
                              value={row.description}
                              onChange={(e) => {
                                const v = e.target.value;
                                setLines((L) =>
                                  L.map((x) => (x.key === row.key ? { ...x, description: v } : x))
                                );
                                clearFieldError(`ligne_${i}_desc`);
                              }}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                              placeholder="Prestation"
                            />
                            {fieldErrors[`ligne_${i}_desc`] ? (
                              <span className="text-[10px] text-rose-400">
                                {fieldErrors[`ligne_${i}_desc`]}
                              </span>
                            ) : null}
                          </td>
                          <td className="py-2 pr-2 align-top">
                            <input
                              type="number"
                              min={0.001}
                              step="any"
                              value={row.quantite}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                setLines((L) =>
                                  L.map((x) =>
                                    x.key === row.key ? { ...x, quantite: v } : x
                                  )
                                );
                                clearFieldError(`ligne_${i}_qte`);
                              }}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                            />
                          </td>
                          <td className="py-2 pr-2 align-top">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={row.prixUnitaire}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                setLines((L) =>
                                  L.map((x) =>
                                    x.key === row.key ? { ...x, prixUnitaire: v } : x
                                  )
                                );
                                clearFieldError(`ligne_${i}_pu`);
                              }}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                            />
                          </td>
                          <td className="py-2 pr-2 align-top">
                            <select
                              value={row.devise}
                              onChange={(e) => {
                                const v = e.target.value as Devise;
                                setLines((L) =>
                                  L.map((x) => (x.key === row.key ? { ...x, devise: v } : x))
                                );
                              }}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                            >
                              <option value="USD">USD</option>
                              <option value="CDF">CDF</option>
                            </select>
                          </td>
                          <td className="py-2 pr-2 align-top">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={row.remise}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                setLines((L) =>
                                  L.map((x) =>
                                    x.key === row.key ? { ...x, remise: Math.max(0, v) } : x
                                  )
                                );
                              }}
                              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm"
                            />
                          </td>
                          <td className="py-2 pr-2 text-right align-middle tabular-nums text-slate-200">
                            {calculateLineTotal({
                              quantite: row.quantite,
                              prixUnitaire: row.prixUnitaire,
                              remise: row.remise,
                            }).toFixed(2)}{" "}
                            {row.devise}
                          </td>
                          <td className="py-2 align-middle">
                            <button
                              type="button"
                              disabled={lines.length <= 1}
                              onClick={() =>
                                setLines((L) => L.filter((x) => x.key !== row.key))
                              }
                              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-800 hover:text-rose-400 disabled:opacity-30"
                              aria-label="Supprimer la ligne"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* C — Totaux + TVA */}
              <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl ring-1 ring-white/5">
                <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-400/90">
                  C. Totaux & TVA
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">Devise document</label>
                    <select
                      value={deviseDefaut}
                      onChange={(e) => setDeviseDefaut(e.target.value as Devise)}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    >
                      <option value="USD">USD</option>
                      <option value="CDF">CDF</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">
                      Taux TVA ({configTva.toFixed(2)} %) — lecture seule
                    </label>
                    <input
                      readOnly
                      value={`${exonereTva ? 0 : configTva} %`}
                      className="w-full cursor-not-allowed rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-400"
                    />
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={exonereTva}
                      onChange={(e) => {
                        setExonereTva(e.target.checked);
                        clearFieldError("motifExoneration");
                      }}
                      className="rounded border-slate-600 bg-slate-950"
                    />
                    Appliquer exonération TVA
                  </label>
                  {exonereTva ? (
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-slate-400">
                        Justification exonération *
                      </label>
                      <textarea
                        value={motifExoneration}
                        onChange={(e) => setMotifExoneration(e.target.value)}
                        rows={2}
                        className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                      />
                      {fieldErrors.motifExoneration ? (
                        <p className="mt-1 text-xs text-rose-400">{fieldErrors.motifExoneration}</p>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="mt-6 grid gap-2 rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Total HT (brut)</span>
                    <span className="tabular-nums text-white">
                      {totals.sousTotalBrut.toFixed(2)} {deviseDefaut}
                    </span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Remise totale</span>
                    <span className="tabular-nums text-amber-200/90">
                      −{totals.remiseTotale.toFixed(2)} {deviseDefaut}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium text-white">
                    <span>Total HT net</span>
                    <span className="tabular-nums">{totals.sousTotalHT.toFixed(2)} {deviseDefaut}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>TVA</span>
                    <span className="tabular-nums">{totals.tva.toFixed(2)} {deviseDefaut}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-800 pt-2 text-base font-bold text-emerald-400">
                    <span>Total TTC</span>
                    <span className="tabular-nums">{totals.totalTTC.toFixed(2)} {deviseDefaut}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Net à payer</span>
                    <span className="tabular-nums">{totals.netAPayer.toFixed(2)} {deviseDefaut}</span>
                  </div>
                  {fieldErrors.totaux ? (
                    <p className="text-xs text-rose-400">{fieldErrors.totaux}</p>
                  ) : null}
                </div>
              </section>

              {/* D — Conditions */}
              <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl ring-1 ring-white/5">
                <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-400/90">
                  D. Conditions commerciales
                </h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">
                      Validité du devis (jours)
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={validiteProformaJours}
                      onChange={(e) => setValiditeProformaJours(Number(e.target.value))}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-400">
                      Délai de paiement (jours)
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={delaiPaiementJours}
                      onChange={(e) => setDelaiPaiementJours(Number(e.target.value))}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-400">
                      Conditions de paiement
                    </label>
                    <textarea
                      value={conditionsPaiement}
                      onChange={(e) => setConditionsPaiement(e.target.value)}
                      rows={3}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-400">Notes client</label>
                    <textarea
                      value={notesClient}
                      onChange={(e) => setNotesClient(e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-400">
                      Mention spécifique
                    </label>
                    <textarea
                      value={mentionSpecifique}
                      onChange={(e) => setMentionSpecifique(e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </section>

              {/* E — Pièce jointe */}
              <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl ring-1 ring-white/5">
                <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-emerald-400/90">
                  E. Pièce jointe
                </h2>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-400">
                  <Paperclip className="h-4 w-4" />
                  <input
                    type="file"
                    onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
                    className="text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:px-3 file:py-1.5 file:text-white"
                  />
                </label>
                {attachment ? (
                  <p className="mt-2 text-xs text-slate-500">{attachment.name}</p>
                ) : null}
              </section>

              {/* F — Actions */}
              <section className="flex flex-wrap gap-3 pb-12">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submit("brouillon")}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2.5 text-sm font-semibold hover:bg-slate-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" /> Enregistrer brouillon
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void openPreview()}
                  className="inline-flex items-center gap-2 rounded-xl border border-sky-600/50 bg-sky-950/40 px-4 py-2.5 text-sm font-semibold text-sky-200 hover:bg-sky-900/50 disabled:opacity-50"
                >
                  {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  Aperçu PDF
                </button>
                <button
                  type="button"
                  disabled={submitting || !validationState.isValid}
                  onClick={() => void submit("finaliser")}
                  title={
                    !validationState.isValid
                      ? "Corrigez les erreurs avant de créer la proforma."
                      : undefined
                  }
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer + générer PDF
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submit("envoyer")}
                  className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> Créer / Envoyer
                </button>
              </section>
              {!validationState.isValid ? (
                <p className="text-xs text-amber-300">
                  Corrigez les erreurs avant de créer la proforma.
                </p>
              ) : null}
              {fieldErrors._submit ? (
                <p className="text-sm text-rose-400">{fieldErrors._submit}</p>
              ) : null}
              {fieldErrors._preview ? (
                <p className="text-sm text-rose-400">{fieldErrors._preview}</p>
              ) : null}
            </div>

            {/* Sticky résumé */}
            <aside className="mt-8 hidden lg:sticky lg:top-6 lg:mt-0 lg:block lg:h-fit lg:rounded-2xl lg:border lg:border-slate-800 lg:bg-slate-900/90 lg:p-4 lg:shadow-xl lg:ring-1 lg:ring-white/5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Résumé devis</p>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-500">Client</dt>
                  <dd className="max-w-[160px] truncate text-right text-white">
                    {clientId ? `#${clientId}` : "—"}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-slate-500">Type métier</dt>
                  <dd className="text-right text-white">
                    {TYPE_METIER_OPTIONS.find((t) => t.value === typeMetier)?.label ?? typeMetier}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Lignes</dt>
                  <dd className="tabular-nums text-white">{lines.length}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Total HT</dt>
                  <dd className="tabular-nums text-emerald-300">
                    {totals.sousTotalHT.toFixed(2)} {deviseDefaut}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">TVA</dt>
                  <dd className="tabular-nums text-white">{totals.tva.toFixed(2)} {deviseDefaut}</dd>
                </div>
                <div className="flex justify-between border-t border-slate-800 pt-2 font-semibold">
                  <dt className="text-slate-400">Total TTC</dt>
                  <dd className="tabular-nums text-amber-300">
                    {totals.totalTTC.toFixed(2)} {deviseDefaut}
                  </dd>
                </div>
                <div className="flex flex-wrap gap-1.5 border-t border-slate-800 pt-2">
                  <span
                    className={`rounded px-2 py-0.5 text-[10px] font-semibold ${
                      validationState.status === "INVALIDE"
                        ? "bg-rose-500/20 text-rose-200"
                        : validationState.status === "INCOMPLET"
                          ? "bg-amber-500/20 text-amber-200"
                          : "bg-emerald-500/20 text-emerald-200"
                    }`}
                  >
                    {validationState.status}
                  </span>
                  {terrainRequis ? (
                    <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-200">
                      Terrain requis
                    </span>
                  ) : null}
                </div>
              </dl>

              {validationState.errors.length > 0 ? (
                <div className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3">
                  <p className="text-xs font-semibold text-rose-200">Actions bloquées</p>
                  <ul className="mt-2 space-y-1 text-xs text-rose-100">
                    {validationState.errors.map((issue) => (
                      <li key={issue.code}>• {issue.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {validationState.warnings.length > 0 ? (
                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  <p className="text-xs font-semibold text-amber-200">Points à vérifier</p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-100">
                    {validationState.warnings.map((issue) => (
                      <li key={issue.code}>• {issue.message}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {validationState.errors.length === 0 && validationState.warnings.length === 0 ? (
                <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-100">
                  Devis prêt à être créé ou envoyé.
                </div>
              ) : null}
            </aside>
          </div>
        )}
      </div>

      {previewDto ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
          <div className="sticky top-0 z-[51] mx-auto mb-4 flex max-w-[980px] flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-950/95 px-4 py-3 shadow-xl">
            <div>
              <span className="font-semibold text-white">Aperçu PDF proforma</span>
              {previewDto.incomplete ? (
                <p className="text-xs text-amber-200">Aperçu incomplet : {previewDto.missing.join(", ")}</p>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={printPreviewDocument}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                <Printer className="h-4 w-4" />
                Imprimer
              </button>
              <button
                type="button"
                onClick={closePreview}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
                Fermer
              </button>
            </div>
          </div>
          <div id="proforma-preview-print-root">
            <CommercialProformaPreview data={previewDto} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
